import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from './Button'

interface ConfirmDialogProps {
  isOpen: boolean
  title: string
  message: string
  confirmText?: string
  cancelText?: string
  confirmVariant?: 'primary' | 'danger' | 'secondary'
  onConfirm: () => void
  onCancel: () => void
}

export function ConfirmDialog({
  isOpen,
  title,
  message,
  confirmText,
  cancelText,
  confirmVariant = 'primary',
  onConfirm,
  onCancel
}: ConfirmDialogProps) {
  const { t } = useTranslation()
  
  const defaultConfirmText = confirmText || t('common.confirm')
  const defaultCancelText = cancelText || t('common.cancel')
  // Handle escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onCancel()
      }
    }

    if (isOpen) {
      document.addEventListener('keydown', handleEscape)
      // Prevent body scroll when modal is open
      document.body.style.overflow = 'hidden'
    }

    return () => {
      document.removeEventListener('keydown', handleEscape)
      document.body.style.overflow = 'unset'
    }
  }, [isOpen, onCancel])

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black/50 transition-opacity"
        onClick={onCancel}
      />
      
      {/* Modal */}
      <div className="relative bg-surface border border-border rounded-full shadow-lg max-w-md w-full mx-lg p-0 transform transition-all duration-200 ease-out scale-100 opacity-100">
        {/* Header */}
        <div className="p-lg border-b border-border">
          <h3 className="text-lg font-semibold text-text">{title}</h3>
        </div>
        
        {/* Content */}
        <div className="p-lg">
          <p className="text-text-muted">{message}</p>
        </div>
        
        {/* Actions */}
        <div className="flex gap-sm justify-end p-lg border-t border-border">
          <Button
            variant="tertiary"
            size="sm"
            onClick={onCancel}
          >
            {defaultCancelText}
          </Button>
          <Button
            variant={confirmVariant}
            size="sm"
            onClick={onConfirm}
          >
            {defaultConfirmText}
          </Button>
        </div>
      </div>
    </div>
  )
} 